import React, { useContext, useEffect, useState } from 'react';
import {
  Box, Button, Paper, Typography, Snackbar, Alert, CircularProgress, Chip,
} from '@mui/material';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import { DateTime } from 'luxon';
import { supabase } from '../supabaseClient';
import { AuthContext, MyContext } from '../context/AuthContext';
import { hapticHeavy } from '../lib/haptics';

/**
 * DAILY CHECK-IN BANNER
 *
 * Shown on the home page when the current user is eligible for today's
 * free time bonus. Calls the `claim_daily_credit` Postgres RPC which
 * is idempotent within a UTC day. The bonus scales with the user's
 * current streak so longtime posters get bigger refills.
 *
 * Eligibility check: we look at userRecord.last_daily_credit_at and
 * compare to today's UTC date. If null or older, show the banner.
 */

function isEligibleNow(lastCreditAt: string | null | undefined): boolean {
  if (!lastCreditAt) return true;
  const last = DateTime.fromISO(lastCreditAt).toUTC();
  if (!last.isValid) return true;
  const today = DateTime.utc().startOf('day');
  return last < today;
}

function formatGrant(seconds: number): string {
  if (seconds <= 0) return '0';
  const m = Math.floor(seconds / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  return `${m} min`;
}

function DailyCheckin() {
  const userRecord = useContext<MyContext>(AuthContext).userRecord;
  const [eligible, setEligible] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [toast, setToast] = useState<{ severity: 'success' | 'info' | 'error'; msg: string } | null>(null);
  // Once a user claims in this session, hide the banner immediately —
  // the realtime user-record subscription will eventually update
  // last_daily_credit_at, but we don't want to wait.
  const [claimed, setClaimed] = useState(false);

  useEffect(() => {
    if (!userRecord) {
      setEligible(false);
      return;
    }
    setEligible(isEligibleNow((userRecord as any).last_daily_credit_at));
  }, [userRecord?.id, (userRecord as any)?.last_daily_credit_at]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!userRecord || !eligible || claimed) return null;

  const handleClaim = async () => {
    setClaiming(true);
    try {
      const { data, error } = await supabase.rpc('claim_daily_credit');
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) throw new Error('empty response');
      if (row.already_claimed) {
        setToast({ severity: 'info', msg: 'Already claimed today — come back tomorrow' });
        setEligible(false);
        return;
      }
      const granted = Number(row.granted_seconds ?? 0);
      const streak = Number(row.streak ?? 0);
      hapticHeavy();
      setToast({
        severity: 'success',
        msg: streak >= 7
          ? `☀️ +${formatGrant(granted)} claimed! 🔥 ${streak}d streak bonus`
          : `☀️ +${formatGrant(granted)} added to your wallet`,
      });
      setClaimed(true);
    } catch (e: any) {
      console.error('Daily checkin failed:', e);
      setToast({ severity: 'error', msg: e.message ?? 'Could not claim' });
    } finally {
      setClaiming(false);
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 1.75,
          borderRadius: 2,
          border: '1px solid rgba(255,193,7,0.45)',
          background: (t) => t.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(255,193,7,0.06), rgba(0,0,0,0))'
            : 'linear-gradient(135deg, rgba(255,193,7,0.08), rgba(255,255,255,0))',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          <WbSunnyIcon sx={{ color: '#ffc107', fontSize: 26 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: '"VT323", monospace',
                letterSpacing: 1.5,
                fontSize: '1rem',
                color: '#ffc107',
                fontWeight: 700,
                lineHeight: 1.1,
              }}
            >
              DAILY CHECK-IN AVAILABLE
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Claim your free wallet refill — bigger if you're on a streak.
            </Typography>
          </Box>
          <Chip
            label="FREE"
            size="small"
            sx={{
              height: 18,
              fontSize: '0.62rem',
              fontFamily: '"VT323", monospace',
              letterSpacing: 1.5,
              bgcolor: '#ffc107',
              color: '#1a1a1a',
              fontWeight: 700,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleClaim}
            disabled={claiming}
            startIcon={claiming ? <CircularProgress size={14} sx={{ color: '#1a1a1a' }} /> : undefined}
            sx={{
              bgcolor: '#ffc107',
              color: '#1a1a1a',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': { bgcolor: '#ffb300' },
            }}
          >
            {claiming ? 'Claiming…' : 'Claim'}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={!!toast}
        autoHideDuration={4500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}

export default DailyCheckin;
